import { type ReactElement, useCallback } from 'react';
import { useRefreshTime } from './use-refresh';
import { TransactionStatus } from '@constants/transaction-status';
import { transactionErrorMap } from '@constants/transaction-error-map';
// import * as Sentry from '@sentry/react';
import { Transaction } from '@mysten/sui/transactions';
import { transactionModel } from '@models/transaction.model';
import {
  useCurrentAccount,
  useCurrentWallet,
  useSignAndExecuteTransaction,
  useSignTransaction,
  useSuiClient,
} from '@mysten/dapp-kit';
import { STASHED_WALLET_NAME, ZkSendLinkBuilder } from '@mysten/zksend';
import { toast } from 'react-toastify';
import ToastLoading from '@components/toast/loading';
import ToastError from '@components/toast/error';
import ToastSuccess from '@components/toast/success';

export const useTransaction = (func?: string) => {
  const client = useSuiClient();
  const { mutateAsync: signAndExecuteTransaction } = useSignAndExecuteTransaction();
  const { mutateAsync: signTransaction } = useSignTransaction();
  const currentAccount = useCurrentAccount();
  const { currentWallet } = useCurrentWallet();

  const { updateRefresh } = useRefreshTime();

  const transact = useCallback(
    async (
      description: string | ReactElement,
      fn: () => Promise<Transaction | undefined>,
    ): Promise<{ status: boolean; digest?: string }> => {
      transactionModel.update({
        status: TransactionStatus.confirm,
        fun: func,
      });

      try {
        const txb = await fn();
        if (!txb) {
          transactionModel.update({
            status: undefined,
            fun: undefined,
          });
          return {
            status: false,
          };
        }

        if (description) {
          toast(<ToastLoading description={description} />);
        }

        if (currentWallet?.name === STASHED_WALLET_NAME && currentAccount) {
          const link = new ZkSendLinkBuilder({
            sender: currentAccount.address,
            client,
          });
          link.createSendTransaction({
            transaction: txb,
          });
        }
        const { signature, bytes } = await signTransaction({ transaction: txb });

        const now = Date.now();
        const { digest } = await client.executeTransactionBlock({
          signature: signature,
          transactionBlock: bytes,
        });

        const end = Date.now() - now;
        const response = await client.waitForTransaction({
          digest,
          options: {
            showEffects: true,
          },
        });
        const effects = response.effects;
        if (effects?.status.status === 'success') {
          transactionModel.update({
            status: TransactionStatus.success,
          });
          toast(<ToastSuccess description={description} time={end / 1000} digest={digest} />);
          updateRefresh(30);
          return {
            status: true,
            digest,
          };
        } else {
          toast(<ToastError description={effects?.status.error || ''} />);
          throw new Error(effects?.status.error || '');
        }
      } catch (e) {
        const errorMsg = transformMessage((e as Error).message);
        console.error('Transaction Exception:\n\n', (e as Error).stack || (e as Error).message);
        toast(<ToastError description={errorMsg} />);
        transactionModel.update({
          status: TransactionStatus.fail,
        });
        // if (!isRejectedFromUser(errorMsg)) {
        //   Sentry.captureMessage((e as Error).stack || (e as Error).message);
        // }
        return {
          status: false,
        };
      }
    },
    [
      updateRefresh,
      signAndExecuteTransaction,
      signTransaction,
      client,
      currentAccount,
      currentWallet,
    ],
  );

  return transact;
};

const isRejectedFromUser = (msg: string) => {
  return (
    msg.toLocaleLowerCase().indexOf('rejected from user') >= 0 ||
    msg.toLocaleLowerCase().indexOf('user rejection') >= 0
  );
};

const transformMessage = (errorMessage: string) => {
  if (isRejectedFromUser(errorMessage)) return errorMessage;

  const matches = errorMessage.match(/name: Identifier\("(\w+)"\).*},\s*(\d+)\)/) as
    | [string, string]
    | null;
  if (!matches) return 'Transaction was not processed. Please try again.';

  const [swapRouter, value] = matches;
  const mappedMsg = transactionErrorMap[swapRouter]?.[value];
  if (mappedMsg) return mappedMsg;

  return `${
    swapRouter.toLowerCase().indexOf('swap') >= 0 ? 'Swap' : 'Transaction'
  } was not processed. Please try again.`;
};
